import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import Login from "./components/Login";
import Signup from "./components/Signup";
import VerifyEmailOtp from "./components/VerifyEmailOtp";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import ToastContainer from "./components/ToastContainer";
import { AuthService } from "./utils/auth";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsOfUse from "./components/legal/TermsOfUse";
import LegalNotice from "./components/legal/LegalNotice";
import NotFound from "./components/NotFound";
import LandingV2 from "./v2/LandingV2";

// The dashboard (booking flows, calendar, maps, carousel…) is by far the
// heaviest part of the app: load it only when someone actually goes there.
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const OAuthCallback = lazy(() => import("./components/OAuthCallback"));

/** Minimal ink screen while a lazy chunk loads. */
const ChunkFallback: React.FC = () => (
  <div className="min-h-screen bg-[#0B0B0C]" aria-busy="true" />
);
import ServicesPageV2 from "./v2/pages/ServicesPageV2";
import ServiceDetailV2 from "./v2/pages/ServiceDetailV2";
import PricingPageV2 from "./v2/pages/PricingPageV2";
import HowItWorksPageV2 from "./v2/pages/HowItWorksPageV2";
import DevisPageV2 from "./v2/pages/DevisPageV2";
import SurMesurePageV2 from "./v2/pages/SurMesurePageV2";
import B2BPageV2 from "./v2/pages/B2BPageV2";
import FAQPageV2 from "./v2/pages/FAQPageV2";
import ShotsPageV2 from "./v2/pages/ShotsPageV2";

/** Old /v2 URLs: strip the prefix (the editorial design is the site now). */
const StripV2: React.FC = () => {
  const location = useLocation();
  const target = location.pathname.replace(/\/v2(?=\/|$)/, "") || "/";
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

// Protected route wrapper: redirects to /:lang/login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { lang } = useParams();
  if (!AuthService.isAuthenticated()) {
    const targetLang = lang || "fr";
    return <Navigate to={`/${targetLang}/login`} replace />;
  }
  return children;
};
// Language Route Wrapper Component
const LanguageRouteWrapper: React.FC = () => {
  return (
    <Routes>
      {/* Language-based routes */}
      <Route path=":lang" element={<LandingV2 />} />
      <Route path=":lang/login" element={<Login />} />
      <Route path=":lang/signup" element={<Signup />} />
      <Route path=":lang/verify-email" element={<VerifyEmailOtp />} />
      <Route
        path=":lang/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={<ChunkFallback />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path=":lang/service/:serviceId" element={<ServiceDetailV2 />} />
      <Route path=":lang/services" element={<ServicesPageV2 />} />
      <Route path=":lang/tarifs" element={<PricingPageV2 />} />
      <Route path=":lang/comment-ca-marche" element={<HowItWorksPageV2 />} />
      <Route path=":lang/devis" element={<DevisPageV2 />} />
      <Route path=":lang/sur-mesure" element={<SurMesurePageV2 />} />
      <Route path=":lang/entreprises" element={<B2BPageV2 />} />
      <Route path=":lang/faq" element={<FAQPageV2 />} />
      <Route path=":lang/shots" element={<ShotsPageV2 />} />
      {/* The before/after catalog was folded into the video reel */}
      <Route path=":lang/catalog" element={<CatalogRedirect />} />
      {/* Legal pages */}
      <Route path=":lang/privacy" element={<PrivacyPolicy />} />
      <Route path=":lang/terms" element={<TermsOfUse />} />
      <Route path=":lang/legal" element={<LegalNotice />} />
      {/* Former preview URLs */}
      <Route path=":lang/v2/*" element={<StripV2 />} />
      <Route path=":lang/v2" element={<StripV2 />} />
      <Route path="/v2" element={<Navigate to="/fr" replace />} />
      {/* Language catch-all to 404 */}
      <Route path=":lang/*" element={<NotFound />} />
      <Route
        path="/auth/callback"
        element={
          <Suspense fallback={<ChunkFallback />}>
            <OAuthCallback />
          </Suspense>
        }
      />
      {/* Redirect root to French */}
      <Route path="/" element={<Navigate to="/fr" replace />} />
      {/* Catch-all route for unmatched paths */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App(): JSX.Element {
  return (
    <Router>
      {/* Scroll to top on route change (affects legal pages and all navigations) */}
      <ScrollToTop />
      <ToastProvider>
        <LanguageProvider>
          <div className="App">
            {/* Listen for ban notifications set by AuthService and display a toast */}
            <BannedNoticeListener />
            <LanguageRouteWrapper />
            <ToastContainer />
          </div>
        </LanguageProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;

// Helper placed at end of file to keep App.tsx self-contained
function ScrollToTop(): JSX.Element | null {
  const location = useLocation();
  useEffect(() => {
    // Jump to top on each pathname change. Use 'auto' (instant) or switch to 'smooth' if preferred.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);
  return null;
}

// Legacy catalog URL → the video reel
function CatalogRedirect(): JSX.Element {
  const { lang } = useParams();
  return <Navigate to={`/${lang || "fr"}/shots`} replace />;
}

// Shows a toast if a ban flag was set by AuthService after a 403 "User is banned"
function BannedNoticeListener(): JSX.Element | null {
  const { error } = useToast();
  useEffect(() => {
    const flag = localStorage.getItem("banned_notice");
    if (flag) {
      // Clear immediately to avoid duplicate toasts
      localStorage.removeItem("banned_notice");
      error(
        "Votre compte a été suspendu. Veuillez contacter le support si besoin.",
      );
    }
  }, [error]);
  return null;
}
